import React, { useState, useContext, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material/styles";
import { useHistory } from "react-router-dom";
import { useMutation } from "react-query";
import { useTreeData } from "@react-stately/data";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import LinearProgress from "@mui/material/LinearProgress";

import AppContext from "../appContext";
import * as api from "../services";
import importQuestions from "./importQuestions";
import CustomToolbar from "./Components/CustomToolbar";
import TitleQuestion from "./TitleQuestion";
import QuestionList from "./QuestionList";
import { getDuplicate, subTypes } from "./itemTypes";
import getQuestion from "./getQuestion";
import detailsModel from "./detailsModel";
import AddButton from "./AddButton";
import useLockMutation from "../useLockMutation";
import EditorMessage from "../Components/EditorMessage";
import RouterPrompt from "../Components/RouterPrompt";
import { logging } from "../tools";

const QuestionContainer = styled(Container, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  padding: theme.spacing(3),
  margin: "auto",
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  maxWidth: theme.breakpoints.values.md,
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    margin: "inherit",
  }),
}));

export default function Editor(props) {
  const history = useHistory();
  const { t } = useTranslation();
  // local state
  const {
    app: {
      client: { id: clientId, editable, writeAccess },
      project: { id: projectId },
      sessionId,
    },
  } = useContext(AppContext);
  let questionTree = useTreeData({
    initialItems: [
      {
        id: "root",
        items: [],
      },
    ],
    initialSelectedKeys: [],
    getKey: (item) => item.id,
    getChildren: (item) => item.items,
  });
  //console.log(questionTree.getItem("root").children); // display the questionTree
  const interactionId = props.location.state?.interactionId ?? -1; // typeof number = public interaction / typeof string = local interaction
  const disabled = editable === 0 && typeof interactionId === "string";
  const isEditorDisabled = writeAccess === 0;
  const [details, setDetails] = useState(detailsModel()); // interaction details (e.g., fullQuestion / description)
  const [creator, setCreator] = useState({
    firstName: "",
    lastName: "",
    code: "",
  }); // display details in public editor. Edit interaction if code is my practitioner's code
  const [selected, setSelected] = useState("title"); // focus on the selected question
  const [expandedKeys, setExpandedKeys] = useState([]); // contains keys of expanded questions
  const [shiftToLeft, setShiftToLeft] = useState(false); // shift the questionList to the left if error popover is open
  const [isBlocking, setIsBlocking] = useState(false); // show leave prompt
  const [questionIndex, setQuestionIndex] = useState(1); // unique ids for the questions
  const [updateIndex, setUpdateIndex] = useState(1); // count the updates
  const [lastSaved, setLastSaved] = useState("");

  const initEditor = (interaction) => {
    const readQuestion = importQuestions(interaction); // import the questions
    setDetails(readQuestion.details); // update the details
    questionTree.update("root", { items: readQuestion.questions }); // update the tree
    handleCurateView(readQuestion.details.itemExtra.interactionView); // curate view
    setIsBlocking(false);
  };
  //constructor
  useEffect(() => {
    // get the interaction from memory (interaction isn't saved in local or public library)
    if (props.location.state?.interaction) {
      logging("Unsaved interaction from cache");
      initEditor(props.location.state.interaction);
    } else if (
      props.location.state?.interactionId &&
      !["new", -1].includes(props.location.state.interactionId)
    ) {
      logging("Load interaction from server");
      setSelected(""); // do not focus on the title
      loadMutation.mutate(props.location.state.interactionId);
    } else if (
      props.location.state?.interactionId &&
      ["new", -1].includes(props.location.state.interactionId)
    ) {
      logging("New public or local interaction");
    } else {
      logging("Can't import interaction");
    }
  }, []);

  // Save conflic occurs (RELOAD_EDITOR) or new version is available (RELOAD) and user wants to view the local interaction
  useEffect(() => {
    if (
      props.location.state?.interactionId &&
      ["RELOAD_EDITOR", "RELOAD"].includes(props.location.state?.type)
    ) {
      logging("RELOAD_EDITOR");
      loadMutation.mutate(props.location.state.interactionId);
      // erease the "type" flag in state
      history.replace({
        state: {
          interactionId: props.location.state.interactionId,
          interaction: null,
        },
      });
    }
  }, [props]);

  // Lock the editor when editing questions
  const lockmutation = useLockMutation();
  useEffect(() => {
    // User have to make a change (updateIndex > 1) in the client editor (typeof interactionId === string)
    if (updateIndex === 2 && typeof interactionId === "string") {
      lockmutation.mutate();
    }
    if (updateIndex > 2 && typeof interactionId === "string") {
      const timeOutId = setTimeout(() => {
        lockmutation.mutate();
      }, 60000);
      return () => {
        clearTimeout(timeOutId);
      };
    }
  }, [updateIndex]);

  // server state
  const loadMutation = useMutation(
    (iId) =>
      typeof iId === "number"
        ? api.getPublicInteraction({ interactionId: iId, projectId, sessionId })
        : api.getLocalInteraction({
            clientId,
            interactionId: iId,
            projectId,
            sessionId,
          }),
    {
      onSuccess: (data, iId) => {
        if (data?.interaction) {
          if (typeof iId === "string") {
            setLastSaved(data.lastSaved);
          }
          const interaction =
            typeof iId === "number"
              ? { ...data.interaction, fullQuestion: data?.name ?? "" }
              : data.interaction;
          initEditor(interaction);
          if (data?.from) setCreator(data.from);
        }
      },
    }
  );

  const onSelect = (key) => setSelected(key);
  const incremetQuestionIndex = () => {
    setQuestionIndex(questionIndex + 1);
  };
  const handleBlock = (type) => {
    if (
      [
        "INSERT_QUESTION_ROOT",
        "INSERT_QUESTION_AFTER",
        "DUPLICATE_QUESTION",
        "REMOVE_QUESTION",
        "UPDATE_QUESTION",
        "DROP_QUESTION",
        "UPDATE_DETAILS",
        "CURATE_VIEW",
      ].includes(type)
    ) {
      setIsBlocking(true);
      setUpdateIndex((prev) => prev + 1);
    }
  };
  const handleCurateView = (payload) => {
    let keys = [];
    if (payload === 1) {
      // expand questionTree to display curate children
      const expandTree = (initialItems, parent) => {
        initialItems.forEach((item) => {
          if (
            item.children.length > 0 &&
            item.value?.original?.itemExtra?.curate?.show === 0 &&
            item.value?.original?.itemExtra?.curate?.hideTree === 0
          ) {
            keys.push(item.key);
            expandTree(item.children, item);
          }
        });
      };
      expandTree(questionTree.getItem("root").children);
    }
    // set the expanded keys
    setExpandedKeys(keys);
    // set the interactionView to curate
    setDetails((prev) => ({
      ...prev,
      itemExtra: {
        ...prev.itemExtra,
        interactionView: payload, // 1 = curate, 0 = full editor
      },
    }));
  };

  const handleQuestionTreeApi = useCallback(
    ({ type, payload }) => {
      const key = payload?.key ?? "";
      const value = payload?.value ?? { type: "undefined" };
      const toParentKey = payload?.toParentKey ?? "";
      const index = payload?.index ?? 0;
      const question = questionTree.getItem(key);
      const childQuestion = question?.value ?? null;
      const parentQuestion = toParentKey
        ? questionTree.getItem(toParentKey).value
        : null;
      let items = [];
      const buildTree = (initialItems, isDuplicate = false) => {
        return initialItems.map((item) => {
          let node = item.value;
          node.items = buildTree(item.children);
          return isDuplicate ? getDuplicate(node) : node;
        });
      };
      switch (type) {
        case "INSERT_QUESTION_ROOT":
          const q = getQuestion(payload, questionIndex);
          questionTree.append("root", q);
          onSelect(`${q.id}#question`);
          incremetQuestionIndex();
          break;
        case "INSERT_QUESTION_AFTER":
          const addQuestionAfter = getQuestion(value.type, questionIndex);
          questionTree.insertAfter(key, addQuestionAfter);
          onSelect(`${addQuestionAfter.id}#question`);
          incremetQuestionIndex();
          break;
        case "DUPLICATE_QUESTION":
          // duplicate the children
          items =
            question.children.length > 0
              ? buildTree(question.children, true)
              : [];
          // duplicate the question
          const duplicateQuestion = getDuplicate({ ...value, items: items });
          questionTree.insertAfter(key, duplicateQuestion);
          onSelect(`${duplicateQuestion.id}#question`);
          break;
        case "UPDATE_QUESTION":
          items =
            question.children.length > 0 ? buildTree(question.children) : [];
          questionTree.update(key, { ...question.value, ...value, items });
          break;
        case "REMOVE_QUESTION":
          questionTree.remove(payload);
          break;
        case "MAKE_CONDITIONAL":
          questionTree.move(key, toParentKey, index);
          setExpandedKeys((prev) => [...prev, toParentKey]);
          if (parentQuestion.type !== subTypes.container) {
            handleQuestionTreeApi({
              type: "UPDATE_QUESTION",
              payload: {
                key,
                value: {
                  conditions: value,
                  defaultValue: payload.defaultValue,
                },
              },
            });
          }
          break;
        case "MAKE_UNCONDITIONAL":
          questionTree.move(key, toParentKey, index);
          handleQuestionTreeApi({
            type: "UPDATE_QUESTION",
            payload: { key, value: { conditions: [] } },
          });
          break;
        case "INSERT_CONDITIONAL_AFTER":
          const newConditionalQuestion = getQuestion(value.type, questionIndex);
          const buildConitionalTree = (initialItems) => {
            return initialItems.map((item) => {
              let node = item.value;
              node.items = buildConitionalTree(item.children);
              return node;
            });
          };
          items =
            question?.children?.length > 0
              ? [
                  ...buildConitionalTree(question.children),
                  newConditionalQuestion,
                ]
              : [newConditionalQuestion];
          questionTree.update(key, {
            ...question.value,
            items: items,
          });
          setExpandedKeys((prev) => [...prev, key]);
          onSelect(`${newConditionalQuestion.id}#question`);
          incremetQuestionIndex();
          break;
        case "SELECT_QUESTION":
          onSelect(payload);
          break;
        case "DROP_QUESTION":
          questionTree.move(key, questionTree.getItem(key).parentKey, index);
          break;
        case "COLLAPSE_QUESTIONS":
          if (question.children.length > 0) {
            if (expandedKeys.includes(key)) {
              // delete
              setExpandedKeys((prev) => prev.filter((k) => k !== key));
            }
          }
          break;
        case "TOGGLE_EXPAND_QUESTION":
          if (Boolean(details.itemExtra.interactionView)) {
            // curated mode: search child keys
            const expandTree = (initialItems) => {
              initialItems.forEach((item) => {
                if (
                  item.children.length > 0 &&
                  item.value?.original?.itemExtra?.curate?.show === 0 &&
                  item.value?.original?.itemExtra?.curate?.hideTree === 0
                ) {
                  if (expandedKeys.includes(item.key)) {
                    // delete
                    setExpandedKeys((prev) =>
                      prev.filter((k) => k !== item.key)
                    );
                  } else {
                    //add
                    setExpandedKeys((prev) => [...prev, item.key]);
                  }
                  expandTree(item.children, item);
                }
              });
            };
            expandTree(question.children);
          }
          if (expandedKeys.includes(key)) {
            // delete
            setExpandedKeys((prev) => prev.filter((k) => k !== key));
          } else {
            //add
            setExpandedKeys((prev) => [...prev, key]);
          }
          break;
        // given a child id, toggle all the parent questions open
        case "SCROLL_TO_QUESTION":
          const questionKey = payload.split("#")[0];
          const getParentKey = (key) => {
            const parentKey = questionTree.getItem(key).parentKey;
            if (parentKey !== "root") {
              setExpandedKeys((prev) => [...prev, parentKey]);
              getParentKey(parentKey);
            }
          };
          getParentKey(questionKey);
          onSelect(payload);
          const violation = document.getElementById(questionKey);
          if (violation) {
            window.scrollTo({
              top: violation.offsetTop - 100,
              behavior: "smooth",
            });
          }
          break;
        case "SHIFT_TO_LEFT":
          setShiftToLeft(payload);
          break;
        case "CURATE_VIEW":
          handleCurateView(payload);
          break;
        default:
          break;
      }
      handleBlock(type);
    },
    [questionTree]
  );
  if (loadMutation.isLoading) return <LinearProgress />;
  return (
    <Box height={"100%"}>
      <RouterPrompt when={isBlocking} />
      {typeof interactionId === "string" ? (
        <EditorMessage lastSaved={lastSaved} />
      ) : null}
      <CustomToolbar
        questionTree={questionTree}
        selected={selected}
        details={details}
        interactionId={interactionId}
        handleQuestionTreeApi={handleQuestionTreeApi}
        setDetails={setDetails}
        creator={creator}
        setCreator={setCreator}
        isBlocking={isBlocking}
        setIsBlocking={setIsBlocking}
        disabled={disabled}
        setLastSaved={setLastSaved}
      />
      <QuestionContainer maxWidth="md" open={shiftToLeft}>
        <TitleQuestion
          selected={selected}
          onSelect={onSelect}
          details={details}
          setDetails={setDetails}
          handleBlock={handleBlock}
          disabled={disabled || isEditorDisabled}
        />
        <QuestionList
          questions={questionTree.getItem("root").children}
          handleQuestionTreeApi={handleQuestionTreeApi}
          expandedKeys={expandedKeys}
          selected={selected}
          isCurate={Boolean(details.itemExtra.interactionView)}
          disabled={disabled || isEditorDisabled}
        />
        <Box paddingTop={3} />
        {/** Don't show add button if editor is disabled (writeAccess = 0 or read only rights) and curatemode is not active */}
        {!(disabled || isEditorDisabled) &&
        !Boolean(details.itemExtra.interactionView) ? (
          <AddButton
            addHandler={(questionType) =>
              handleQuestionTreeApi({
                type: "INSERT_QUESTION_ROOT",
                payload: questionType,
              })
            }
            buttonId={"split-button"}
            handleQuestionTreeApi={handleQuestionTreeApi}
            question={
              questionTree.getItem(selected.split("#")?.[0])?.value ?? // selected question
              questionTree.getItem("root").children?.[
                questionTree.getItem("root").children?.length - 1
              ]?.value // last question
            }
          />
        ) : null}
      </QuestionContainer>
    </Box>
  );
}
